import { useParams } from 'react-router-dom';
import { useApplication } from '~/data';
import { useSchoolCustomizations } from '~/data/useSchoolCustomizations';
import { Flex, Spinner } from '@applyboard/crystal-ui';
import { ApplicationDetailsLayout } from './layouts';
import { useWindowDocumentTitle } from '~/lib/useWindowDocumentTitle';
import { ApplicationDetailContext } from './context';
import { ErrorHandler, ErrorPage } from '../ErrorPage';
import { useCourses } from '~/data/useCourses';

export function ApplicationDetailsPage() {
  useWindowDocumentTitle('Application Details');
  const applicationId = useApplicationId();
  const { application, isLoadingApplication, applicationError, isApplicationError } = useApplication(applicationId);
  const {
    schoolCustomizations,
    isLoadingSchoolCustomizations,
    isSchoolCustomizationsError,
    schoolCustomizationsError,
  } = useSchoolCustomizations();
  const { courses, isLoadingCourses, isCoursesError } = useCourses();

  if (
    !isApplicationError &&
    !isSchoolCustomizationsError &&
    !isCoursesError &&
    (isLoadingApplication || isLoadingSchoolCustomizations || isLoadingCourses)
  ) {
    return (
      <Flex p={5} gap={5} align="center" direction="column" role="progressbar">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isApplicationError) {
    return <ErrorHandler error={applicationError} customMessage="Failed to fetch the application details." />;
  }

  if (isSchoolCustomizationsError) {
    return <ErrorHandler error={schoolCustomizationsError} customMessage="Failed to fetch school customizations." />;
  }

  if (!application || !schoolCustomizations || !courses) {
    return <ErrorPage customMessage="Failed to fetch page props." />;
  }
  return (
    <ApplicationDetailContext application={application} schoolCustomizations={schoolCustomizations} courses={courses}>
      <ApplicationDetailsLayout />
    </ApplicationDetailContext>
  );
}

function useApplicationId() {
  const { id } = useParams() as { id: string };
  return id;
}
